import React, { useState, useEffect } from 'react';
import { formatDateTime, formatNumber, formatCurrency, formatRelative } from '../utils/format';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { isSuperuser } from '../utils/permissions';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../components/AuthContext';
import { notificationState } from '../utils/partnerNotifications';
import DataTable from '../components/DataTable';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function SuperuserPanel() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [activeTab, setActiveTab] = useState('notifications');

  // Platform Settings (active gateway + PayTR + Iyzico + SMTP) State
  const [platform, setPlatform] = useState(null);
  const [platformDraft, setPlatformDraft] = useState({
    active_gateway: 'paytr',
    paytr: { merchant_id: '', key: '', salt: '', live_mode: false },
    iyzico: { api_key: '', secret_key: '', live_mode: false },
    smtp: { host: '', port: 587, user: '', password: '', from: '', use_ssl: false, test_recipient: '' },
    notifications: { emails: '' },
  });
  const [platformSaving, setPlatformSaving] = useState(false);
  const [notifyTesting, setNotifyTesting] = useState(false);

  const fetchPlatformSettings = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/superuser/platform-settings`);
      setPlatform(data);
      setPlatformDraft({
        active_gateway: data.active_gateway || 'paytr',
        paytr: {
          merchant_id: data.paytr.merchant_id || '',
          key: '',  // never pre-filled (masked)
          salt: '',
          live_mode: !!data.paytr.live_mode,
        },
        iyzico: {
          api_key: data.iyzico?.api_key_preview || '',
          secret_key: '',
          live_mode: !!data.iyzico?.live_mode,
        },
        smtp: {
          host: data.smtp.host || '',
          port: data.smtp.port || 587,
          user: data.smtp.user || '',
          password: '',
          from: data.smtp.from || '',
          use_ssl: !!data.smtp.use_ssl,
          test_recipient: data.smtp.test_recipient || '',
        },
        notifications: {
          emails: (data.notifications?.emails || []).join(', '),
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('superuser.platform.loadFailed'));
    }
  };

  const savePlatformSettings = async () => {
    setPlatformSaving(true);
    try {
      // Only send fields the user actually edited; secrets blank = no change.
      const body = {
        active_gateway: platformDraft.active_gateway,
        paytr: {
          merchant_id: platformDraft.paytr.merchant_id,
          live_mode: platformDraft.paytr.live_mode,
        },
        iyzico: {
          api_key: platformDraft.iyzico.api_key,
          live_mode: platformDraft.iyzico.live_mode,
        },
        smtp: {
          host: platformDraft.smtp.host,
          port: platformDraft.smtp.port,
          user: platformDraft.smtp.user,
          from: platformDraft.smtp.from,
          use_ssl: platformDraft.smtp.use_ssl,
          test_recipient: platformDraft.smtp.test_recipient,
        },
        notifications: {
          emails: platformDraft.notifications.emails,
        },
      };
      if (platformDraft.paytr.key) body.paytr.key = platformDraft.paytr.key;
      if (platformDraft.paytr.salt) body.paytr.salt = platformDraft.paytr.salt;
      if (platformDraft.iyzico.secret_key) body.iyzico.secret_key = platformDraft.iyzico.secret_key;
      if (platformDraft.smtp.password) body.smtp.password = platformDraft.smtp.password;
      const { data } = await axios.put(`${API_URL}/api/superuser/platform-settings`, body);
      setPlatform(data);
      setPlatformDraft((d) => ({
        ...d,
        notifications: { emails: (data.notifications?.emails || []).join(', ') },
        paytr: { ...d.paytr, key: '', salt: '' },
        iyzico: { ...d.iyzico, secret_key: '' },
        smtp: { ...d.smtp, password: '' },
      }));
      toast.success(t('superuser.platform.saved'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('superuser.common.saveFailed'));
    } finally {
      setPlatformSaving(false);
    }
  };

  // SaaS Pricing State (billing_plan_settings)
  const [saasPricing, setSaasPricing] = useState(null);
  const [saasSaving, setSaasSaving] = useState(false);

  const fetchSaasPricing = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/billing/plan-pricing`);
      // Defensive copy so the user can edit freely without mutating the
      // server snapshot, and add stable React keys to module rows.
      setSaasPricing({
        ...data,
        available_modules: (data.available_modules || []).map((m, i) => ({ ...m, _row: i })),
        volume_discount_tiers: (data.volume_discount_tiers || []).map((t, i) => ({ ...t, _row: i })),
      });
    } catch (e) {
      toast.error(t('superuser.pricing.loadFailed'));
    }
  };

  const saveSaasPricing = async () => {
    if (!saasPricing) return;
    setSaasSaving(true);
    try {
      // Yearly is auto-derived from monthly + yearly_discount_pct.
      // Compute on save so the persisted store stays consistent
      // regardless of whatever stale price_yearly the form started with.
      const pct = Number(saasPricing.yearly_discount_pct || 0);
      const computeY = (m) => Math.round(Number(m || 0) * 12 * (1 - pct / 100) * 100) / 100;
      const body = {
        currency: saasPricing.currency,
        trial_days: Number(saasPricing.trial_days || 14),
        trial_user_limit: Number(saasPricing.trial_user_limit || 5),
        base_user_count_included: Number(saasPricing.base_user_count_included || 1),
        per_user_price_monthly: Number(saasPricing.per_user_price_monthly || 0),
        per_user_price_yearly: computeY(saasPricing.per_user_price_monthly),
        per_module_price_monthly: Number(saasPricing.per_module_price_monthly || 0),
        per_module_price_yearly: computeY(saasPricing.per_module_price_monthly),
        yearly_discount_pct: pct,
        available_modules: (saasPricing.available_modules || []).map((m) => ({
          key: m.key, name: m.name,
          price_monthly: Number(m.price_monthly || 0),
          price_yearly: computeY(m.price_monthly),
        })),
        volume_discount_tiers: (saasPricing.volume_discount_tiers || []).map((t) => ({
          min_users: Number(t.min_users || 0),
          discount_pct: Number(t.discount_pct || 0),
        })),
      };
      await axios.put(`${API_URL}/api/billing/plan-pricing`, body);
      toast.success(t('superuser.pricing.saved'));
      fetchSaasPricing();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('superuser.common.saveFailed'));
    } finally {
      setSaasSaving(false);
    }
  };

  const updateModuleField = (rowIdx, field, value) => {
    setSaasPricing((p) => ({
      ...p,
      available_modules: p.available_modules.map((m) =>
        m._row === rowIdx ? { ...m, [field]: value } : m
      ),
    }));
  };

  const removeModule = (rowIdx) => {
    setSaasPricing((p) => ({
      ...p,
      available_modules: p.available_modules.filter((m) => m._row !== rowIdx),
    }));
  };

  const addModule = () => {
    setSaasPricing((p) => ({
      ...p,
      available_modules: [
        ...p.available_modules,
        { _row: Date.now(), key: '', name: '', price_monthly: 0, price_yearly: 0 },
      ],
    }));
  };

  const updateTierField = (rowIdx, field, value) => {
    setSaasPricing((p) => ({
      ...p,
      volume_discount_tiers: (p.volume_discount_tiers || []).map((t) =>
        t._row === rowIdx ? { ...t, [field]: value } : t
      ),
    }));
  };
  const removeTier = (rowIdx) => {
    setSaasPricing((p) => ({
      ...p,
      volume_discount_tiers: (p.volume_discount_tiers || []).filter((t) => t._row !== rowIdx),
    }));
  };
  const addTier = () => {
    setSaasPricing((p) => ({
      ...p,
      volume_discount_tiers: [
        ...(p.volume_discount_tiers || []),
        { _row: Date.now(), min_users: 10, discount_pct: 5 },
      ],
    }));
  };

  // License Events (audit) State
  const [licenseRows, setLicenseRows] = useState(null);
  const [licenseStatusFilter, setLicenseStatusFilter] = useState('');

  const fetchLicenseEvents = async () => {
    try {
      const params = licenseStatusFilter ? { status: licenseStatusFilter } : {};
      const { data } = await axios.get(`${API_URL}/api/superuser/platform-settings/license-events`, { params });
      setLicenseRows(data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('superuser.licenseHistory.loadFailed'));
    }
  };

  const [rawDetail, setRawDetail] = useState(null);  // full order doc for the modal
  const openRawDetail = async (merchOid) => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/superuser/platform-settings/license-events/${merchOid}/raw`
      );
      setRawDetail(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('superuser.licenseHistory.detailFailed'));
    }
  };

  // Yearly = monthly × 12 × (1 - yearly_discount_pct/100). Auto-derived
  // so the operator only edits monthly; yearly is computed and shown
  // read-only. Avoids the drift from independently editing two columns.
  const computeYearly = React.useCallback((monthly) => {
    const m = Number(monthly || 0);
    const pct = Number(saasPricing?.yearly_discount_pct || 0);
    const yearly = m * 12 * (1 - pct / 100);
    return Math.round(yearly * 100) / 100;  // 2 decimals
  }, [saasPricing?.yearly_discount_pct]);

  const [refundingOid, setRefundingOid] = useState(null);
  const refundOrder = async (merchOid, totalKurus, currency) => {
    const tlAmount = formatNumber(totalKurus / 100, { minimumFractionDigits: 2 });
    const input = window.prompt(
      t('superuser.refund.amountPrompt', { currency: currency || 'TRY', max: tlAmount }),
      ''
    );
    if (input === null) return;  // user cancelled
    let amount_kurus;
    if (input.trim() === '') {
      amount_kurus = totalKurus;  // full refund
    } else {
      const parsed = parseFloat(input.replace(',', '.'));
      if (isNaN(parsed) || parsed <= 0) { toast.error(t('superuser.refund.invalidAmount')); return; }
      amount_kurus = Math.round(parsed * 100);
      if (amount_kurus > totalKurus) { toast.error(t('superuser.refund.exceedsOriginal')); return; }
    }
    setRefundingOid(merchOid);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/superuser/platform-settings/license-events/${merchOid}/refund`,
        { amount_kurus }
      );
      toast.success(t(data.partial ? 'superuser.refund.partialDone' : 'superuser.refund.done', { gateway: data.gateway, amount: formatNumber(data.amount_kurus / 100, { minimumFractionDigits: 2 }), currency: currency || 'TRY' }));
      fetchLicenseEvents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('superuser.refund.failed'));
    } finally {
      setRefundingOid(null);
    }
  };

  const [mailingTenant, setMailingTenant] = useState(null);
  const sendCardUpdateMail = async (tenantId, companyName) => {
    const note = window.prompt(
      t('superuser.cardMail.prompt', { company: companyName }),
      ''
    );
    if (note === null) return;  // cancelled
    setMailingTenant(tenantId);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/superuser/platform-settings/license-events/${tenantId}/send-update-card-mail`,
        { error_message: note.trim() || undefined }
      );
      if (data.ok) toast.success(t('superuser.cardMail.sent', { to: data.to || t('superuser.cardMail.noAddress') }));
      else toast.error(t('superuser.cardMail.failed'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('superuser.common.requestFailed'));
    } finally {
      setMailingTenant(null);
    }
  };

  const [refundingRenewalTenant, setRefundingRenewalTenant] = useState(null);
  const refundLastRenewal = async (tenantId, companyName) => {
    const input = window.prompt(
      t('superuser.refund.renewalPrompt', { company: companyName }),
      ''
    );
    if (input === null) return;
    let amount_kurus;
    if (input.trim() === '') {
      amount_kurus = undefined;
    } else {
      const parsed = parseFloat(input.replace(',', '.'));
      if (isNaN(parsed) || parsed <= 0) { toast.error(t('superuser.refund.invalidAmount')); return; }
      amount_kurus = Math.round(parsed * 100);
    }
    setRefundingRenewalTenant(tenantId);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/superuser/platform-settings/license-events/${tenantId}/refund-last-renewal`,
        amount_kurus ? { amount_kurus } : {}
      );
      toast.success(t('superuser.refund.renewalDone', { gateway: data.gateway, amount: formatNumber(data.amount_kurus / 100, { minimumFractionDigits: 2 }), currency: data.currency }));
      fetchLicenseEvents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('superuser.refund.failed'));
    } finally {
      setRefundingRenewalTenant(null);
    }
  };

  const [chargingTenant, setChargingTenant] = useState(null);
  const chargeTenantNow = async (tenantId, companyName) => {
    if (!window.confirm(t('superuser.charge.confirm', { company: companyName }))) return;
    setChargingTenant(tenantId);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/superuser/platform-settings/license-events/${tenantId}/charge-now`
      );
      if (data.ok) {
        toast.success(t('superuser.charge.success', { gateway: data.gateway || 'gateway', date: data.expires_at?.slice(0, 10) }));
      } else {
        toast.error(t('superuser.charge.failed', { error: data.error || t('superuser.common.unknownError') }));
      }
      fetchLicenseEvents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('superuser.charge.requestFailed'));
    } finally {
      setChargingTenant(null);
    }
  };

  const sendSmtpTest = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/api/superuser/platform-settings/test-smtp`,
        { to: platformDraft.smtp.test_recipient });
      if (data.ok) toast.success(t('superuserNotify.test_ok', { to: data.to }));
      else toast.error(t('superuser.smtp.testFailedCheckLogs'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('superuser.smtp.testFailed'));
    }
  };

  // Sends to the addresses typed in the box (validated server-side), or to
  // the effective list when the box is empty — so it works before saving.
  const sendNotifyTest = async () => {
    setNotifyTesting(true);
    try {
      const emails = platformDraft.notifications.emails.trim();
      const { data } = await axios.post(
        `${API_URL}/api/superuser/platform-settings/test-notification-email`,
        emails ? { emails } : {},
      );
      if (data.ok) toast.success(t('superuserNotify.test_ok', { to: (data.to || []).join(', ') }));
      else toast.error(t('superuserNotify.test_fail', { error: data.error || '?' }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('superuserNotify.test_fail', { error: '?' }));
    } finally {
      setNotifyTesting(false);
    }
  };

  const messageTemplates = [
    { id: 1, text: `🚀 ${t('superuser.notifications.templates.update')}` },
    { id: 2, text: `⚙️ ${t('superuser.notifications.templates.newFeatures')}` },
    { id: 3, text: `🔧 ${t('superuser.notifications.templates.maintenance')}` },
    { id: 4, text: `📦 ${t('superuser.notifications.templates.installing')}` },
    { id: 5, text: `✨ ${t('superuser.notifications.templates.performance')}` }
  ];

  // Fetch settings on tab switch
  useEffect(() => {
    if (activeTab === 'platform') {
      fetchPlatformSettings();
    }
    if (activeTab === 'saas_pricing') {
      fetchSaasPricing();
    }
    if (activeTab === 'license_history') {
      fetchLicenseEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Dashboard summary strip — fetched once on mount, refreshable.
  const [dashStats, setDashStats] = useState(null);
  const fetchDashStats = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/superuser/platform-settings/dashboard-stats`);
      setDashStats(data);
    } catch {
      // best-effort widget — silent on failure
    }
  };
  useEffect(() => {
    fetchDashStats();
  }, []);

  const handleDeploymentNotification = async () => {
    setShowMessageModal(true);
  };

  const sendNotification = async () => {
    const message = customMessage || selectedTemplate || messageTemplates[0].text;

    if (!window.confirm(t('superuser.notifications.sendConfirm', { message }))) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/notify-deployment`, {
        message: message
      });
      toast.success(t('superuser.notifications.sent', { count: response.data.recipients }), 5000);
      setShowMessageModal(false);
      setCustomMessage('');
      setSelectedTemplate('');
    } catch (error) {
      toast.error(t('superuser.notifications.sendFailed', { error: error.response?.data?.detail || error.message }));
    } finally {
      setLoading(false);
    }
  };

  const LICENSE_STATUS_BADGE = {
    consumed: 'bg-green-100 text-green-800',
    pending: 'bg-amber-100 text-amber-800',
    failed: 'bg-red-100 text-red-800',
    amount_mismatch: 'bg-orange-100 text-orange-800',
    refunded: 'bg-purple-100 text-purple-800',
    partial_refund: 'bg-purple-100 text-purple-800',
  };
  const licenseStatusText = (r) => t(`superuser.licenseHistory.statuses.${r.status}`, { defaultValue: r.status });
  const licenseNowText = (r) => (r.license_status_now ? t(`superuser.licenseStatus.${r.license_status_now}`, { defaultValue: r.license_status_now }) : '');
  const licenseColumns = [
    { key: 'created_at', header: t('superuser.licenseHistory.time'), sortable: true,
      value: (r) => (r.callback_at || r.created_at || '').slice(0, 16).replace('T', ' '),
      render: (r) => (
        <span className="whitespace-nowrap text-xs text-gray-600">
          {(r.callback_at || r.created_at || '').slice(0, 16).replace('T', ' ')}
        </span>
      ) },
    { key: 'company_name', header: t('superuser.licenseHistory.company'), sortable: true,
      render: (r) => (
        <div>
          <div className="font-medium">{r.company_name || '—'}</div>
          <div className="text-xs text-gray-400">{r.signup_source}</div>
        </div>
      ) },
    { key: 'gateway', header: t('superuser.licenseHistory.gateway'), sortable: true,
      render: (r) => <span className="text-xs">{r.gateway || '—'}</span> },
    { key: 'total_kurus', header: t('superuser.licenseHistory.amount'), align: 'right', sortable: true,
      value: (r) => (r.total_kurus || 0) / 100,
      render: (r) => (
        <div className="whitespace-nowrap">
          {formatNumber((r.total_kurus || 0) / 100, { minimumFractionDigits: 2 })} TRY
          {r.charged_currency && r.charged_currency !== 'TRY' && r.charged_amount_kurus && (
            <div className="text-xs text-blue-700">
              ≈ {formatNumber(r.charged_amount_kurus / 100, { minimumFractionDigits: 2 })}{' '}
              {r.charged_currency}
            </div>
          )}
        </div>
      ) },
    { key: 'cart', header: t('superuser.licenseHistory.cart'),
      value: (r) => (r.cart?.cycle ? t(`superuser.licenseHistory.cycle.${r.cart.cycle}`, { defaultValue: r.cart.cycle }) : ''),
      render: (r) => {
        const cart = r.cart || {};
        return (
          <span className="text-xs">
            {cart.cycle && <span className="font-medium">{t(`superuser.licenseHistory.cycle.${cart.cycle}`, { defaultValue: cart.cycle })}</span>}
            {cart.extra_users != null && <span> · {t('superuser.licenseHistory.extraUsers', { count: cart.extra_users })}</span>}
            {Array.isArray(cart.extra_modules) && <span> · {t('superuser.licenseHistory.modulesCount', { count: cart.extra_modules.length })}</span>}
          </span>
        );
      } },
    { key: 'status', header: t('superuser.licenseHistory.status'), sortable: true, value: licenseStatusText,
      render: (r) => (
        <div>
          <span className={`text-xs px-2 py-1 rounded-full ${LICENSE_STATUS_BADGE[r.status] || 'bg-gray-100 text-gray-700'}`}>{licenseStatusText(r)}</span>
          {r.failed_reason && (
            <div className="text-xs text-red-600 mt-1 max-w-xs truncate" title={String(r.failed_reason)}>
              {String(r.failed_reason).slice(0, 60)}
            </div>
          )}
        </div>
      ) },
    { key: 'license_status_now', header: t('superuser.licenseHistory.licenseNow'), sortable: true, value: licenseNowText,
      render: (r) => (
        <div className="text-xs">
          <div>{licenseNowText(r) || '—'}</div>
          {r.license_expires_at && (
            <div className="text-gray-400">{r.license_expires_at.slice(0, 10)}</div>
          )}
          {r.retry_count > 0 && (
            <div className="text-amber-600">{t('superuser.dashboard.retry', { count: r.retry_count, max: 2 })}</div>
          )}
        </div>
      ) },
  ];
  const licenseActions = (r) => (
    <div className="inline-flex flex-col items-end text-xs space-y-1">
      {/* Renewal nudge — only meaningful for tenants in
          past_due / suspended states. */}
      {['past_due', 'suspended', 'trial_expired'].includes(r.license_status_now) && (
        <>
          <button onClick={() => chargeTenantNow(r.tenant_id, r.company_name)}
                  disabled={chargingTenant === r.tenant_id}
                  className="block text-xs px-2 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
            {chargingTenant === r.tenant_id ? '…' : `⚡ ${t('superuser.licenseHistory.chargeNow')}`}
          </button>
          <button onClick={() => sendCardUpdateMail(r.tenant_id, r.company_name)}
                  disabled={mailingTenant === r.tenant_id}
                  className="block text-xs px-2 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50">
            {mailingTenant === r.tenant_id ? '…' : `✉ ${t('superuser.licenseHistory.sendMail')}`}
          </button>
        </>
      )}
      {/* Refund — only for orders that actually charged. */}
      {['consumed', 'amount_mismatch'].includes(r.status) && (
        <button onClick={() => refundOrder(r.merch_oid, r.total_kurus, r.currency)}
                disabled={refundingOid === r.merch_oid}
                className="block text-xs px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50">
          {refundingOid === r.merch_oid ? '…' : `↩ ${t('superuser.licenseHistory.refund')}`}
        </button>
      )}
      {/* Refund the last *renewal* charge (separate from
          the initial-checkout refund). Backend 409s if
          no Iyzico handle is on the company doc. */}
      {r.tenant_id && r.license_status_now === 'active' && (
        <button onClick={() => refundLastRenewal(r.tenant_id, r.company_name)}
                disabled={refundingRenewalTenant === r.tenant_id}
                className="block text-xs px-2 py-1 rounded bg-rose-700 hover:bg-rose-800 text-white disabled:opacity-50">
          {refundingRenewalTenant === r.tenant_id ? '…' : `↩ ${t('superuser.licenseHistory.refundRenewal')}`}
        </button>
      )}
      <button onClick={() => openRawDetail(r.merch_oid)}
              className="block text-xs px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700">
        🔍 {t('superuser.licenseHistory.detail')}
      </button>
    </div>
  );
  const refundColumns = [
    { key: 'refunded_at', header: t('superuser.licenseHistory.date'),
      value: (rf) => (rf.refunded_at || '').slice(0, 16).replace('T', ' '),
      render: (rf) => (rf.refunded_at || '').slice(0, 16).replace('T', ' ') },
    { key: 'amount_kurus', header: t('superuser.licenseHistory.amount'), align: 'right',
      value: (rf) => rf.amount_kurus / 100,
      render: (rf) => (
        <span className="font-medium whitespace-nowrap">
          {formatNumber(rf.amount_kurus / 100, { minimumFractionDigits: 2 })} {rf.currency}
        </span>
      ) },
    { key: 'gateway', header: t('superuser.licenseHistory.gateway') },
  ];

  if (!isSuperuser(user)) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {t('superuser.accessDenied')}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="page-title text-3xl">🔐 {t('superuser.title')}</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">{t('superuser.subtitle')}</p>
      </div>

      {/* Dashboard Summary */}
      {dashStats && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              ['total', dashStats.total_companies, 'bg-gray-100 text-gray-700'],
              ['active', dashStats.by_status.active, 'bg-green-100 text-green-800'],
              ['trial', dashStats.by_status.trial, 'bg-blue-100 text-blue-800'],
              ['trialExpired', dashStats.by_status.trial_expired, 'bg-amber-100 text-amber-800'],
              ['pastDue', dashStats.by_status.past_due, 'bg-orange-100 text-orange-800'],
              ['suspended', dashStats.by_status.suspended, 'bg-red-100 text-red-800'],
            ].map(([label, value, cls]) => (
              <div key={label} className={`rounded-lg px-3 py-2 ${cls}`}>
                <div className="text-xs uppercase opacity-70">{t(`superuser.dashboard.${label}`)}</div>
                <div className="text-2xl font-bold">{value}</div>
              </div>
            ))}
          </div>
          <div className="card p-3 flex flex-wrap items-center gap-4 text-sm">
            <span className="text-gray-500">{t('superuser.dashboard.last24h')}</span>
            <span>🛒 {t('superuser.dashboard.checkoutsStarted')} <b>{dashStats.last_24h.checkouts_started}</b></span>
            <span className="text-green-700">✓ {t('superuser.dashboard.checkoutsPaid')} <b>{dashStats.last_24h.checkouts_paid}</b></span>
            <span className="text-red-700">✕ {t('superuser.dashboard.checkoutsFailed')} <b>{dashStats.last_24h.checkouts_failed}</b></span>
            <span className="text-blue-700">✚ {t('superuser.dashboard.signups')} <b>{dashStats.last_24h.signups ?? 0}</b></span>
            <button onClick={fetchDashStats} className="ml-auto text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200">
              ↻ {t('superuser.common.refresh')}
            </button>
          </div>
          {dashStats.recent_signups && (
            <div className="card p-3">
              <div className="text-sm font-semibold mb-2">{t('superuser.dashboard.newAccounts', { days: 14 })}</div>
              <DataTable
                id="superuser-recent-signups"
                rows={dashStats.recent_signups}
                rowKey="tenant_id"
                minimal
                exportName="yeni-hesaplar"
                emptyText={t('superuser.dashboard.noNewAccounts')}
                columns={[
                  { key: 'name', header: t('superuser.tenants.company'), sortable: true,
                    render: (r) => <span className="font-medium">{r.name || '—'}</span> },
                  { key: 'contact_email', header: t('superuser.tenants.email'), sortable: true,
                    render: (r) => <span className="text-gray-600">{r.contact_email || '—'}</span> },
                  { key: 'created_at', header: t('superuser.tenants.signedUp'), sortable: true,
                    value: (r) => r.created_at || '',
                    render: (r) => <span title={formatDateTime(r.created_at)}>{formatRelative(r.created_at)}</span> },
                  { key: 'license_status', header: t('superuser.licenseHistory.status'), sortable: true,
                    value: (r) => t(`superuser.licenseStatus.${r.license_status || 'other'}`, { defaultValue: r.license_status }) },
                  { key: 'users', header: t('superuser.tenants.usersCol'), align: 'right', sortable: true },
                  { key: 'last_seen_at', header: t('users.lastSeen'), sortable: true,
                    value: (r) => r.last_seen_at || '',
                    render: (r) => (r.last_seen_at
                      ? <span title={formatDateTime(r.last_seen_at)} data-testid={`signup-last-seen-${r.tenant_id}`}>{formatRelative(r.last_seen_at)}</span>
                      : <span className="text-gray-400">{t('users.never')}</span>) },
                ]}
              />
            </div>
          )}
          {dashStats.pressure?.length > 0 && (
            <div className="card p-3 border-l-4 border-amber-500 bg-amber-50/30">
              <div className="text-sm font-semibold text-amber-900 mb-2">
                ⚠ {t('superuser.dashboard.renewalPressure', { count: dashStats.pressure.length })}
              </div>
              <ul className="text-xs space-y-1">
                {dashStats.pressure.slice(0, 5).map((p) => (
                  <li key={p.tenant_id} className="flex justify-between gap-4">
                    <span><b>{p.name}</b> · {p.license_status} · {t('superuser.dashboard.retry', { count: p.retry_count, max: 2 })}</span>
                    <span className="text-gray-500 truncate max-w-xs" title={p.last_error}>
                      {p.last_error?.slice(0, 60)}
                    </span>
                  </li>
                ))}
                {dashStats.pressure.length > 5 && (
                  <li className="text-amber-700 text-xs italic">
                    {t('superuser.dashboard.morePressure', { count: dashStats.pressure.length - 5 })}
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('notifications')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'notifications'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:border-gray-300 dark:border-surface-700'
            }`}
          >
            📢 {t('superuser.tabs.notifications')}
          </button>
          <button
            onClick={() => setActiveTab('platform')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'platform'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:border-gray-300 dark:border-surface-700'
            }`}
          >
            ⚙️ {t('superuser.tabs.platform')}
          </button>
          <button
            onClick={() => setActiveTab('saas_pricing')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'saas_pricing'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:border-gray-300 dark:border-surface-700'
            }`}
          >
            🏷️ {t('superuser.tabs.pricing')}
          </button>
          <button
            onClick={() => setActiveTab('license_history')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'license_history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:border-gray-300 dark:border-surface-700'
            }`}
          >
            📒 {t('superuser.tabs.licenseHistory')}
          </button>
          <button
            onClick={() => setActiveTab('tenants')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tenants'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:border-gray-300 dark:border-surface-700'
            }`}
          >
            🏢 {t('superuser.tabs.tenants')}
          </button>
          <button
            onClick={() => setActiveTab('partners')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'partners'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:border-gray-300 dark:border-surface-700'
            }`}
          >
            🤝 {t('superuser.tabs.partners')}
          </button>
        </nav>
      </div>

      {activeTab === 'tenants' && <TenantsTab />}
      {activeTab === 'partners' && <PartnersTab />}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="space-y-6">
          {/* Deployment Notification Card */}
          <div className="card p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center text-3xl">
                🚀
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('superuser.notifications.title')}</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-4">{t('superuser.notifications.description')}</p>
                <button
                  onClick={handleDeploymentNotification}
                  disabled={loading}
                  className={`px-6 py-3 rounded-lg font-semibold transition ${
                    loading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-orange-500 hover:bg-orange-600 text-white'
                  }`}
                >
                  {loading ? `⏳ ${t('superuser.common.sending')}` : `📢 ${t('superuser.notifications.announceUpdate')}`}
                </button>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">⚠️ {t('superuser.notifications.deployHint')}</p>
              </div>
            </div>
          </div>

          {/* System Info Card */}
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-4">📊 {t('superuser.notifications.systemInfo')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white dark:bg-gray-800 bg-opacity-20 rounded-lg p-4">
                <div className="text-sm opacity-90">{t('superuser.notifications.websocketConnections')}</div>
                <div className="text-3xl font-bold mt-1">{t('superuser.notifications.realtime')}</div>
              </div>
              <div className="bg-white dark:bg-gray-800 bg-opacity-20 rounded-lg p-4">
                <div className="text-sm opacity-90">{t('superuser.notifications.notificationSystem')}</div>
                <div className="text-3xl font-bold mt-1">✅ {t('superuser.common.active')}</div>
              </div>
              <div className="bg-white dark:bg-gray-800 bg-opacity-20 rounded-lg p-4">
                <div className="text-sm opacity-90">{t('superuser.notifications.deployment')}</div>
                <div className="text-3xl font-bold mt-1">{t('superuser.notifications.push')}</div>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-bold text-blue-900 mb-3">📖 {t('superuser.notifications.instructions')}</h3>
            <ol className="space-y-2 text-blue-800">
              <li className="flex gap-2">
                <span className="font-bold">1.</span>
                <span>{t('superuser.notifications.steps.press', { button: t('superuser.notifications.announceUpdate') })}</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold">2.</span>
                <span>{t('superuser.notifications.steps.choose')}</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold">3.</span>
                <span>{t('superuser.notifications.steps.broadcast')}</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold">4.</span>
                <span>{t('superuser.notifications.steps.countdown')}</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold">5.</span>
                <span>{t('superuser.notifications.steps.deploy')}</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold">6.</span>
                <span>{t('superuser.notifications.steps.restart')}</span>
              </li>
            </ol>
          </div>
        </div>
      )}


      {/* Message Selection Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={(e) => { if (e.target === e.currentTarget) setShowMessageModal(false); }}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="page-title text-2xl mb-4">📝 {t('superuser.notifications.chooseMessage')}</h2>

            {/* Template Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">{t('superuser.notifications.templatesLabel')}</label>
              <div className="space-y-2">
                {messageTemplates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => {
                      setSelectedTemplate(template.text);
                      setCustomMessage('');
                    }}
                    className={`p-3 border-2 rounded-lg cursor-pointer transition ${
                      selectedTemplate === template.text
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 dark:border-gray-700 hover:border-orange-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={selectedTemplate === template.text}
                        onChange={() => {}}
                        className="text-orange-500"
                      />
                      <span className="text-gray-900 dark:text-gray-100">{template.text}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Custom Message */}
            <div className="mb-6">
              <label className="form-label">{t('superuser.notifications.customMessage')}</label>
              <textarea
                value={customMessage}
                onChange={(e) => {
                  setCustomMessage(e.target.value);
                  setSelectedTemplate('');
                }}
                placeholder={t('superuser.notifications.customMessagePlaceholder')}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500"
              />
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">💡 {t('superuser.notifications.emojiTip')} (🚀 ⚙️ 🔧 ✨)</p>
            </div>

            {/* Preview */}
            <div className="mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-orange-500 p-4 rounded">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{t('superuser.notifications.preview')}</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {customMessage || selectedTemplate || messageTemplates[0].text}
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowMessageModal(false);
                  setCustomMessage('');
                  setSelectedTemplate('');
                }}
                className="flex-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 text-gray-700 dark:text-gray-300 font-semibold py-3 px-4 rounded-lg transition"
              >
                {t('superuser.common.cancel')}
              </button>
              <button
                onClick={sendNotification}
                disabled={loading}
                className={`flex-1 font-semibold py-3 px-4 rounded-lg transition ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-orange-500 hover:bg-orange-600 text-white'
                }`}
              >
                {loading ? `⏳ ${t('superuser.common.sending')}` : `📢 ${t('superuser.notifications.send')}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'platform' && (
        <div className="space-y-6">
          {!platform ? (
            <div className="text-center py-12 text-gray-500">{t('superuser.common.loading')}</div>
          ) : (
            <>
              <div className="card p-5 space-y-3">
                <h2 className="text-lg font-semibold">🚦 {t('superuser.platform.activeGateway')}</h2>
                <p className="text-xs text-gray-500">
                  {t('superuser.platform.activeGatewayHelp')}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {[['paytr', 'PayTR'], ['iyzico', 'Iyzico']].map(([v, label]) => (
                    <button key={v}
                      onClick={() => setPlatformDraft({ ...platformDraft, active_gateway: v })}
                      className={`border rounded-lg p-3 text-left ${platformDraft.active_gateway === v ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/30' : 'border-gray-200 dark:border-surface-700'}`}>
                      <div className="font-medium">{label}</div>
                      <div className="text-xs text-gray-500">
                        {v === 'paytr'
                          ? (platformDraft.paytr.merchant_id ? `✓ ${t('superuser.platform.configured')}` : `— ${t('superuser.platform.notConfigured')}`)
                          : (platform.iyzico?.api_key_set ? `✓ ${t('superuser.platform.configured')}` : `— ${t('superuser.platform.notConfigured')}`)}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="card p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">💳 PayTR ({t('superuser.platform.platformAccount')})</h2>
                  <span className={`text-xs px-2 py-1 rounded-full ${platformDraft.paytr.live_mode ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    {platformDraft.paytr.live_mode ? t('superuser.platform.live') : t('superuser.platform.test')}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  {t('superuser.platform.paytrHelp')}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-sm">Merchant ID</span>
                    <input className="input w-full" value={platformDraft.paytr.merchant_id}
                      onChange={(e) => setPlatformDraft({ ...platformDraft, paytr: { ...platformDraft.paytr, merchant_id: e.target.value } })}
                      placeholder="123456" />
                  </label>
                  <label className="block">
                    <span className="text-sm">Merchant Key {platform.paytr.key_set && <span className="text-xs text-green-600">({t('superuser.platform.storedBadge')})</span>}</span>
                    <input type="password" className="input w-full" value={platformDraft.paytr.key}
                      onChange={(e) => setPlatformDraft({ ...platformDraft, paytr: { ...platformDraft.paytr, key: e.target.value } })}
                      placeholder={platform.paytr.key_set ? `••••••• (${t('superuser.platform.keepIfEmpty')})` : ''} />
                  </label>
                  <label className="block md:col-span-2">
                    <span className="text-sm">Merchant Salt {platform.paytr.salt_set && <span className="text-xs text-green-600">({t('superuser.platform.storedBadge')})</span>}</span>
                    <input type="password" className="input w-full" value={platformDraft.paytr.salt}
                      onChange={(e) => setPlatformDraft({ ...platformDraft, paytr: { ...platformDraft.paytr, salt: e.target.value } })}
                      placeholder={platform.paytr.salt_set ? '•••••••' : ''} />
                  </label>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={platformDraft.paytr.live_mode}
                    onChange={(e) => setPlatformDraft({ ...platformDraft, paytr: { ...platformDraft.paytr, live_mode: e.target.checked } })} />
                  <span><b>{t('superuser.platform.liveMode')}</b> — {t('superuser.platform.paytrLiveHelp')}</span>
                </label>
              </div>

              <div className="card p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">💎 Iyzico ({t('superuser.platform.platformAccount')})</h2>
                  <span className={`text-xs px-2 py-1 rounded-full ${platformDraft.iyzico.live_mode ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    {platformDraft.iyzico.live_mode ? t('superuser.platform.live') : t('superuser.platform.testSandbox')}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  {t('superuser.platform.iyzicoHelp')}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-sm">API Key {platform.iyzico?.api_key_set && <span className="text-xs text-green-600">({t('superuser.platform.storedBadge')})</span>}</span>
                    <input className="input w-full" value={platformDraft.iyzico.api_key}
                      onChange={(e) => setPlatformDraft({ ...platformDraft, iyzico: { ...platformDraft.iyzico, api_key: e.target.value } })}
                      placeholder={t('superuser.platform.iyzicoApiKeyPlaceholder')} />
                  </label>
                  <label className="block">
                    <span className="text-sm">Secret Key {platform.iyzico?.secret_key_set && <span className="text-xs text-green-600">({t('superuser.platform.storedBadge')})</span>}</span>
                    <input type="password" className="input w-full" value={platformDraft.iyzico.secret_key}
                      onChange={(e) => setPlatformDraft({ ...platformDraft, iyzico: { ...platformDraft.iyzico, secret_key: e.target.value } })}
                      placeholder={platform.iyzico?.secret_key_set ? `••••••• (${t('superuser.platform.keepIfEmpty')})` : ''} />
                  </label>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={platformDraft.iyzico.live_mode}
                    onChange={(e) => setPlatformDraft({ ...platformDraft, iyzico: { ...platformDraft.iyzico, live_mode: e.target.checked } })} />
                  <span><b>{t('superuser.platform.liveMode')}</b> — {t('superuser.platform.iyzicoLiveHelp')}</span>
                </label>
              </div>

              <div className="card p-5 space-y-4">
                <h2 className="text-lg font-semibold">📧 {t('superuser.smtp.title')}</h2>
                <p className="text-xs text-gray-500">
                  {t('superuser.smtp.help')}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-sm">SMTP Host</span>
                    <input className="input w-full" value={platformDraft.smtp.host}
                      onChange={(e) => setPlatformDraft({ ...platformDraft, smtp: { ...platformDraft.smtp, host: e.target.value } })}
                      placeholder="smtp.gmail.com" />
                  </label>
                  <label className="block">
                    <span className="text-sm">{t('superuser.smtp.port')}</span>
                    <input type="number" className="input w-full" value={platformDraft.smtp.port}
                      onChange={(e) => setPlatformDraft({ ...platformDraft, smtp: { ...platformDraft.smtp, port: Number(e.target.value || 587) } })} />
                  </label>
                  <label className="block">
                    <span className="text-sm">{t('superuser.smtp.user')}</span>
                    <input className="input w-full" value={platformDraft.smtp.user}
                      onChange={(e) => setPlatformDraft({ ...platformDraft, smtp: { ...platformDraft.smtp, user: e.target.value } })}
                      placeholder="noreply@runvya.com" />
                  </label>
                  <label className="block">
                    <span className="text-sm">{t('superuser.smtp.password')} {platform.smtp.password_set && <span className="text-xs text-green-600">({t('superuser.platform.storedBadge')})</span>}</span>
                    <input type="password" className="input w-full" value={platformDraft.smtp.password}
                      onChange={(e) => setPlatformDraft({ ...platformDraft, smtp: { ...platformDraft.smtp, password: e.target.value } })}
                      placeholder={platform.smtp.password_set ? '•••••••' : ''} />
                  </label>
                  <label className="block md:col-span-2">
                    <span className="text-sm">{t('superuser.smtp.from', { example: "'Runvya <noreply@runvya.com>'" })}</span>
                    <input className="input w-full" value={platformDraft.smtp.from}
                      onChange={(e) => setPlatformDraft({ ...platformDraft, smtp: { ...platformDraft.smtp, from: e.target.value } })} />
                  </label>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={platformDraft.smtp.use_ssl}
                    onChange={(e) => setPlatformDraft({ ...platformDraft, smtp: { ...platformDraft.smtp, use_ssl: e.target.checked } })} />
                  <span>{t('superuser.smtp.useSsl')}</span>
                </label>
                <div className="border-t pt-3 space-y-2">
                  <label className="block">
                    <span className="text-sm">{t('superuser.smtp.testRecipient')}</span>
                    <input type="email" className="input w-full" value={platformDraft.smtp.test_recipient}
                      onChange={(e) => setPlatformDraft({ ...platformDraft, smtp: { ...platformDraft.smtp, test_recipient: e.target.value } })}
                      placeholder="test@example.com" />
                  </label>
                  <p className="text-xs text-gray-500">
                    {t('superuser.smtp.testHelp', { button: t('superuser.smtp.sendTest') })}
                  </p>
                  <button onClick={sendSmtpTest} disabled={!platform.smtp.password_set && !platformDraft.smtp.password}
                    className="btn-secondary text-sm">
                    📤 {t('superuser.smtp.sendTest')}
                  </button>
                </div>
              </div>

              <div className="card p-5 space-y-3" data-testid="notify-email-card">
                <h2 className="text-lg font-semibold">{t('superuserNotify.title')}</h2>
                <label className="block">
                  <span className="text-sm">{t('superuserNotify.label')}</span>
                  <input className="input w-full" name="notify_emails" value={platformDraft.notifications.emails}
                    onChange={(e) => setPlatformDraft({ ...platformDraft, notifications: { emails: e.target.value } })}
                    placeholder={t('superuserNotify.placeholder')} />
                </label>
                <p className="text-xs text-gray-500">
                  {t('superuserNotify.help')}
                </p>
                {platform.notifications?.effective?.length ? (
                  <p className="text-xs text-green-700 dark:text-green-400">
                    {t('superuserNotify.effective')}{' '}
                    <b>{platform.notifications.effective.join(', ')}</b>{' '}
                    ({t(`superuserNotify.source_${platform.notifications.source}`, platform.notifications.source)})
                  </p>
                ) : (
                  <p className="text-xs text-red-700 dark:text-red-400 font-medium">
                    {t('superuserNotify.none')}
                  </p>
                )}
                <div className="flex items-center gap-3 flex-wrap">
                  <button onClick={sendNotifyTest} disabled={notifyTesting} className="btn-secondary text-sm">
                    {t('superuserNotify.test_button')}
                  </button>
                  <span className="text-xs text-gray-500">
                    {t('superuserNotify.test_hint')}
                  </span>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button onClick={fetchPlatformSettings} className="btn-secondary">{t('superuser.common.reset')}</button>
                <button onClick={savePlatformSettings} disabled={platformSaving} className="btn-primary">
                  {platformSaving ? t('superuser.common.saving') : t('superuser.common.save')}
                </button>
              </div>

              <details className="text-xs text-gray-500">
                <summary className="cursor-pointer">{t('superuser.platform.envFallback')}</summary>
                <pre className="mt-2 bg-gray-100 dark:bg-surface-800 p-2 rounded">
{JSON.stringify({ paytr: platform.paytr.env_fallback, smtp: platform.smtp.env_fallback }, null, 2)}
                </pre>
              </details>
            </>
          )}
        </div>
      )}

      {activeTab === 'saas_pricing' && (
        <div className="space-y-6">
          {!saasPricing ? (
            <div className="text-center py-12 text-gray-500">{t('superuser.common.loading')}</div>
          ) : (
            <>
              <div className="card p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">📐 {t('superuser.pricing.general')}</h2>
                  <span className="text-xs text-gray-500">
                    {t('superuser.pricing.generalHelp')}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  <label className="block">
                    <span className="text-sm">{t('superuser.pricing.currency')}</span>
                    <input className="input w-full" value={saasPricing.currency || 'TRY'}
                      onChange={(e) => setSaasPricing({ ...saasPricing, currency: e.target.value })} />
                  </label>
                  <label className="block">
                    <span className="text-sm">{t('superuser.pricing.trialDays')}</span>
                    <input type="number" className="input w-full" value={saasPricing.trial_days}
                      onChange={(e) => setSaasPricing({ ...saasPricing, trial_days: Number(e.target.value || 0) })} />
                  </label>
                  <label className="block">
                    <span className="text-sm">{t('superuser.pricing.trialUserLimit')}</span>
                    <input type="number" min="1" className="input w-full" data-testid="trial-user-limit"
                      value={saasPricing.trial_user_limit ?? 5}
                      title={t('superuser.pricing.trialUserLimitHelp')}
                      onChange={(e) => setSaasPricing({ ...saasPricing, trial_user_limit: Number(e.target.value || 0) })} />
                  </label>
                  <label className="block">
                    <span className="text-sm">{t('superuser.pricing.yearlyDiscount')}</span>
                    <input type="number" className="input w-full" value={saasPricing.yearly_discount_pct}
                      onChange={(e) => setSaasPricing({ ...saasPricing, yearly_discount_pct: Number(e.target.value || 0) })} />
                  </label>
                  <label className="block">
                    <span className="text-sm">{t('superuser.pricing.includedUsers')}</span>
                    <input type="number" className="input w-full" value={saasPricing.base_user_count_included}
                      onChange={(e) => setSaasPricing({ ...saasPricing, base_user_count_included: Number(e.target.value || 0) })} />
                  </label>
                </div>
              </div>

              <div className="card p-5 space-y-3">
                <h2 className="text-lg font-semibold">👤 {t('superuser.pricing.perUser')}</h2>
                <p className="text-xs text-gray-500">
                  {t('superuser.pricing.yearlyFormula')}{' '}
                  {t('superuser.pricing.perUserYearly')} <b>{formatCurrency(computeYearly(saasPricing.per_user_price_monthly), saasPricing.currency || 'TRY')}</b>{t('superuser.pricing.perUserSuffix')}
                </p>
                <label className="block max-w-xs">
                  <span className="text-sm">{t('superuser.pricing.monthlyPerUser')}</span>
                  <input type="number" step="0.01" className="input w-full"
                    value={saasPricing.per_user_price_monthly}
                    onChange={(e) => setSaasPricing({ ...saasPricing, per_user_price_monthly: Number(e.target.value || 0) })} />
                </label>
              </div>

              <div className="card p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">📦 {t('superuser.pricing.modules')}</h2>
                  <button onClick={addModule} className="btn-secondary text-sm">+ {t('superuser.pricing.addModule')}</button>
                </div>
                <p className="text-xs text-gray-500">
                  {t('superuser.pricing.modulesHelp')}
                </p>
                <div className="text-sm">
                  <div className="grid grid-cols-12 gap-2 px-2 py-1 text-gray-500 font-medium">
                    <div className="col-span-3">{t('superuser.pricing.key')}</div>
                    <div className="col-span-4">{t('superuser.pricing.displayName')}</div>
                    <div className="col-span-2 text-right">{t('superuser.pricing.monthlyPrice')}</div>
                    <div className="col-span-2 text-right text-gray-400">{t('superuser.pricing.yearlyAuto')}</div>
                    <div className="col-span-1"></div>
                  </div>
                  {(saasPricing.available_modules || []).map((m) => (
                    <div key={m._row} className="grid grid-cols-12 gap-2 items-center px-2 py-1 border-t">
                      <div className="col-span-3">
                        <input className="input w-full" value={m.key}
                          onChange={(e) => updateModuleField(m._row, 'key', e.target.value)}
                          placeholder="sales" />
                      </div>
                      <div className="col-span-4">
                        <input className="input w-full" value={m.name}
                          onChange={(e) => updateModuleField(m._row, 'name', e.target.value)}
                          placeholder={t('superuser.pricing.displayNamePlaceholder')} />
                      </div>
                      <div className="col-span-2 flex justify-end">
                        <input type="number" step="0.01" className="input w-28 text-right" value={m.price_monthly}
                          onChange={(e) => updateModuleField(m._row, 'price_monthly', Number(e.target.value || 0))} />
                      </div>
                      <div className="col-span-2 text-right text-xs text-gray-400">
                        {formatCurrency(computeYearly(m.price_monthly), saasPricing.currency || 'TRY')}
                      </div>
                      <div className="col-span-1 text-right">
                        <button onClick={() => removeModule(m._row)} className="text-red-500 hover:underline text-xs">{t('superuser.common.remove')}</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">📉 {t('superuser.pricing.volumeDiscount')}</h2>
                  <button onClick={addTier} className="btn-secondary text-sm">+ {t('superuser.pricing.addTier')}</button>
                </div>
                <p className="text-xs text-gray-500">
                  {t('superuser.pricing.volumeHelp')}
                </p>
                <div className="text-sm">
                  <div className="grid grid-cols-12 gap-2 px-2 py-1 text-gray-500 font-medium">
                    <div className="col-span-5">{t('superuser.pricing.minExtraUsers')}</div>
                    <div className="col-span-5">{t('superuser.pricing.discountPct')}</div>
                    <div className="col-span-2"></div>
                  </div>
                  {(saasPricing.volume_discount_tiers || []).length === 0 && (
                    <div className="px-2 py-3 text-gray-400 italic">{t('superuser.pricing.noTiers')}</div>
                  )}
                  {(saasPricing.volume_discount_tiers || []).map((tier) => (
                    <div key={tier._row} className="grid grid-cols-12 gap-2 items-center px-2 py-1 border-t">
                      <div className="col-span-5">
                        <input type="number" min="1" className="input w-32" value={tier.min_users}
                          onChange={(e) => updateTierField(tier._row, 'min_users', Number(e.target.value || 0))} />
                      </div>
                      <div className="col-span-5">
                        <input type="number" min="0" max="100" className="input w-24" value={tier.discount_pct}
                          onChange={(e) => updateTierField(tier._row, 'discount_pct', Number(e.target.value || 0))} />
                      </div>
                      <div className="col-span-2 text-right">
                        <button onClick={() => removeTier(tier._row)} className="text-red-500 hover:underline text-xs">{t('superuser.common.remove')}</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button onClick={fetchSaasPricing} className="btn-secondary">{t('superuser.common.reset')}</button>
                <button onClick={saveSaasPricing} disabled={saasSaving} className="btn-primary">
                  {saasSaving ? t('superuser.common.saving') : t('superuser.common.save')}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'license_history' && (
        <div className="space-y-4">
          <div className="card p-4 flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-600 me-2">{t('superuser.common.filter')}</span>
            {[
              ['', t('superuser.common.all')],
              ['consumed', `✓ ${t('superuser.licenseHistory.filters.consumed')}`],
              ['pending', `⏳ ${t('superuser.licenseHistory.filters.pending')}`],
              ['failed', `✕ ${t('superuser.licenseHistory.filters.failed')}`],
              ['refunded,partial_refund', `↩ ${t('superuser.licenseHistory.filters.refunded')}`],
              ['amount_mismatch', `⚠ ${t('superuser.licenseHistory.filters.amountMismatch')}`],
            ].map(([v, label]) => (
              <button key={v || 'all'}
                onClick={() => { setLicenseStatusFilter(v); setTimeout(fetchLicenseEvents, 0); }}
                className={`text-xs px-3 py-1.5 rounded-full ${licenseStatusFilter === v ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-surface-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200'}`}>
                {label}
              </button>
            ))}
            <button onClick={fetchLicenseEvents} className="text-xs px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 ms-auto">
              ↻ {t('superuser.common.refresh')}
            </button>
          </div>

          <DataTable
            id="superuser-license-history"
            rows={licenseRows || []}
            loading={licenseRows === null}
            rowKey="merch_oid"
            exportName="lisans-gecmisi"
            emptyText={t('superuser.licenseHistory.empty')}
            columns={licenseColumns}
            rowActions={licenseActions}
          />
        </div>
      )}

      {rawDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
             onClick={() => setRawDetail(null)}>
          <div className="bg-white dark:bg-surface-900 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">📄 {t('superuser.licenseHistory.orderDetail')}</h3>
              <button onClick={() => setRawDetail(null)}
                      className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
            </div>
            <div className="p-4 overflow-y-auto">
              <div className="text-xs text-gray-500 mb-2">
                merchant_oid: <code>{rawDetail._id}</code>
              </div>
              {rawDetail._renewal_history && (
                <div className="mb-3 border border-amber-200 bg-amber-50 rounded p-3">
                  <div className="text-sm font-semibold text-amber-900 mb-1">🔁 {t('superuser.licenseHistory.renewalHistory')}</div>
                  <div className="text-xs text-amber-800 space-y-0.5">
                    <div>{t('superuser.licenseHistory.lastCharge')} <b>{rawDetail._renewal_history.last_gateway || '—'}</b>
                      {' '}/ {rawDetail._renewal_history.last_currency || '—'}
                      {rawDetail._renewal_history.last_at && (
                        <span className="text-amber-700"> · {rawDetail._renewal_history.last_at.slice(0, 16).replace('T', ' ')}</span>
                      )}
                    </div>
                    {rawDetail._renewal_history.iyzico_payment_transaction_id && (
                      <div>Iyzico ptx_id: <code className="text-[11px]">{rawDetail._renewal_history.iyzico_payment_transaction_id}</code></div>
                    )}
                  </div>
                  {(rawDetail._renewal_history.refunds || []).length > 0 ? (
                    <div className="mt-2 text-xs">
                      <DataTable
                        id="superuser-renewal-refunds"
                        minimal
                        rows={rawDetail._renewal_history.refunds.map((rf, i) => ({ ...rf, _key: i }))}
                        rowKey="_key"
                        columns={refundColumns}
                      />
                    </div>
                  ) : (
                    <div className="text-xs text-amber-700 mt-1">{t('superuser.licenseHistory.noRefunds')}</div>
                  )}
                </div>
              )}
              <pre className="bg-gray-100 dark:bg-surface-800 p-3 rounded text-xs overflow-x-auto">
{JSON.stringify(rawDetail, null, 2)}
              </pre>
            </div>
            <div className="p-4 border-t flex justify-end">
              <button onClick={() => {
                navigator.clipboard.writeText(JSON.stringify(rawDetail, null, 2)).catch(() => {});
                toast.success(t('superuser.licenseHistory.copied'));
              }} className="btn-secondary text-sm">📋 {t('superuser.licenseHistory.copyJson')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Firmalar tab — superuser tenant lifecycle drilldown.
 * Shows pending signups (form submitted, email not yet clicked) plus
 * the full tenant list filterable by license_status.
 */
function TenantsTab() {
  const { t } = useTranslation();
  const [pending, setPending] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Badge colour per license status; labels: superuser.licenseStatus.<code>.
  const STATUS_LABELS = {
    trial: 'bg-blue-100 text-blue-800',
    active: 'bg-green-100 text-green-800',
    trial_expired: 'bg-amber-100 text-amber-800',
    past_due: 'bg-orange-100 text-orange-800',
    suspended: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-200 text-gray-700',
    other: 'bg-gray-100 text-gray-600',
  };

  const deletePending = async (id, email) => {
    if (!window.confirm(t('superuser.tenants.deletePendingConfirm', { email }))) return;
    try {
      await axios.delete(`${API_URL}/api/superuser/platform-settings/pending-signups/${id}`);
      setPending((arr) => arr.filter((p) => p.id !== id));
    } catch (e) {
      window.alert(e?.response?.data?.detail || t('superuser.common.deleteFailed'));
    }
  };

  const [approvingId, setApprovingId] = React.useState(null);
  const approvePending = async (id, email) => {
    if (!window.confirm(t('superuser.tenants.approveConfirm', { email }))) return;
    setApprovingId(id);
    try {
      await axios.post(`${API_URL}/api/superuser/platform-settings/pending-signups/${id}/approve`);
      setPending((arr) => arr.filter((p) => p.id !== id));
      window.alert(t('superuser.tenants.approved'));
      if (typeof reload === 'function') reload();
    } catch (e) {
      window.alert(e?.response?.data?.detail || t('superuser.tenants.approveFailed'));
    } finally {
      setApprovingId(null);
    }
  };

  const [resendingId, setResendingId] = React.useState(null);
  const resendPending = async (id, email) => {
    if (!window.confirm(t('superuser.tenants.resendConfirm', { email }))) return;
    setResendingId(id);
    try {
      const res = await axios.post(`${API_URL}/api/superuser/platform-settings/pending-signups/${id}/resend`);
      if (res.data?.email_sent) {
        window.alert(t('superuser.tenants.resent'));
      } else {
        window.alert(t('superuser.tenants.resendNoSmtp'));
      }
    } catch (e) {
      window.alert(e?.response?.data?.detail || t('superuser.tenants.resendFailed'));
    } finally {
      setResendingId(null);
    }
  };

  const reload = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (q) params.set('q', q);
      const [tenantRes, p] = await Promise.all([
        axios.get(`${API_URL}/api/superuser/platform-settings/tenants?${params}`),
        axios.get(`${API_URL}/api/superuser/platform-settings/pending-signups?limit=20`),
      ]);
      setTenants(tenantRes.data.rows || []);
      setTotal(tenantRes.data.total || 0);
      setPending(p.data.rows || []);
    } catch (e) {
      // Silent — tab will show empty state
    } finally {
      setLoading(false);
    }
  }, [statusFilter, q]);

  useEffect(() => { reload(); }, [reload]);

  const fmtDate = (s) => (s ? String(s).slice(0, 16).replace('T', ' ') : '—');
  const daysLeft = (iso) => {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    return Math.ceil(ms / 86400000);
  };

  const statusCode = (tn) => (STATUS_LABELS[tn.license_status] ? tn.license_status : 'other');
  const pendingColumns = [
    { key: 'email', header: t('superuser.tenants.email'), sortable: true,
      render: (p) => <span className="font-medium">{p.email}</span> },
    { key: 'company_name', header: t('superuser.tenants.companyName'), sortable: true,
      render: (p) => p.company_name || '—' },
    { key: 'created_at', header: t('superuser.tenants.started'), sortable: true,
      render: (p) => <span className="text-gray-600">{fmtDate(p.created_at)}</span> },
    { key: 'expires_at', header: t('superuser.tenants.expires'), sortable: true,
      render: (p) => <span className="text-gray-600">{fmtDate(p.expires_at)}</span> },
    { key: 'ip', header: 'IP', render: (p) => <span className="text-gray-500 text-xs">{p.ip || '—'}</span> },
  ];
  const tenantColumns = [
    { key: 'name', header: t('superuser.tenants.company'), sortable: true,
      render: (tn) => <span className="font-medium">{tn.name || '—'}</span> },
    { key: 'contact_email', header: t('superuser.tenants.email'), sortable: true,
      render: (tn) => <span className="text-gray-600">{tn.contact_email || '—'}</span> },
    { key: 'license_status', header: t('superuser.licenseHistory.status'), sortable: true,
      value: (tn) => t(`superuser.licenseStatus.${statusCode(tn)}`),
      render: (tn) => (
        <span>
          <span className={`text-xs px-2 py-1 rounded-full ${STATUS_LABELS[statusCode(tn)]}`}>{t(`superuser.licenseStatus.${statusCode(tn)}`)}</span>
          {tn.signup_source === 'self_serve' && (
            <span className="text-[10px] text-gray-400 ms-1">{t('superuser.tenants.selfServe')}</span>
          )}
        </span>
      ) },
    { key: 'users_used', header: t('superuser.tenants.usersCol'), align: 'right', sortable: true,
      value: (tn) => tn.users_used ?? 0,
      render: (tn) => `${tn.users_used ?? 0}${tn.max_users ? ` / ${tn.max_users}` : ''}` },
    { key: 'active_modules_count', header: t('superuser.tenants.modulesCol'), align: 'right', sortable: true },
    { key: 'trial_ends_at', header: t('superuser.tenants.trialEnds'), sortable: true,
      render: (tn) => {
        const trialDl = daysLeft(tn.trial_ends_at);
        return (
          <div className="text-gray-600">
            {tn.trial_ends_at ? `${tn.trial_ends_at.slice(0, 10)}` : '—'}
            {trialDl !== null && tn.license_status === 'trial' && (
              <div className="text-[10px] text-blue-600">{t('superuser.tenants.daysLeft', { count: trialDl })}</div>
            )}
          </div>
        );
      } },
    { key: 'license_expires_at', header: t('superuser.tenants.licenseEnds'), sortable: true,
      render: (tn) => {
        const licDl = daysLeft(tn.license_expires_at);
        return (
          <div className="text-gray-600">
            {tn.license_expires_at ? tn.license_expires_at.slice(0, 10) : '—'}
            {licDl !== null && tn.license_status === 'active' && (
              <div className="text-[10px] text-green-700">{t('superuser.tenants.daysLeft', { count: licDl })}</div>
            )}
          </div>
        );
      } },
    { key: 'license_charged_currency', header: t('superuser.tenants.currency'), sortable: true,
      render: (tn) => <span className="text-xs text-gray-500">{tn.license_charged_currency || '—'}</span> },
    { key: 'created_at', header: t('superuser.tenants.signedUp'), sortable: true,
      render: (tn) => <span className="text-xs text-gray-500">{fmtDate(tn.created_at)}</span> },
  ];

  return (
    <div className="space-y-6 mt-6">
      <div className="bg-gray-50 dark:bg-surface-900 border border-gray-200 dark:border-surface-700 rounded px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
        {t('superuser.tenants.introBefore')}{' '}
        <a className="text-blue-600 underline" href="/companies">{t('superuser.tenants.companiesLink')}</a> /
        <a className="text-blue-600 underline" href="/users"> {t('superuser.tenants.usersLink')}</a> /
        <a className="text-blue-600 underline" href="/licenses"> {t('superuser.tenants.licensesLink')}</a>{' '}
        {t('superuser.tenants.introAfter')}
      </div>

      {/* Pending signups (email sent, not yet clicked) */}
      <div className="card p-5">
        <h2 className="section-title mb-1">📨 {t('superuser.tenants.pendingTitle')}</h2>
        <p className="text-xs text-gray-500 mb-3">
          {t('superuser.tenants.pendingHelp')}
        </p>
        <DataTable
          id="superuser-pending-signups"
          rows={pending}
          rowKey="id"
          exportName="bekleyen-kayitlar"
          emptyText={t('superuser.tenants.noPending')}
          columns={pendingColumns}
          rowActions={(p) => (
            <span className="whitespace-nowrap">
              <button onClick={() => approvePending(p.id, p.email)} disabled={approvingId === p.id}
                      className="text-xs text-green-600 hover:text-green-800 hover:underline disabled:opacity-50 mr-3">
                {approvingId === p.id ? `⏳ ${t('superuser.tenants.approving')}` : `✅ ${t('superuser.tenants.approve')}`}
              </button>
              <button onClick={() => resendPending(p.id, p.email)} disabled={resendingId === p.id}
                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50 mr-3">
                {resendingId === p.id ? `⏳ ${t('superuser.common.sending')}` : `📨 ${t('superuser.tenants.resend')}`}
              </button>
              <button onClick={() => deletePending(p.id, p.email)}
                      className="text-xs text-red-600 hover:text-red-800 hover:underline">
                🗑 {t('superuser.common.delete')}
              </button>
            </span>
          )}
        />
      </div>

      {/* Tenant lifecycle table */}
      <div className="card p-5">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <h2 className="section-title">🏢 {t('superuser.tenants.lifecycleTitle')}</h2>
            <p className="text-xs text-gray-500">{t('superuser.tenants.count', { count: total })}</p>
          </div>
          <input type="search" placeholder={t('superuser.tenants.searchPlaceholder')} value={q}
                 onChange={(e) => setQ(e.target.value)}
                 className="input text-sm" />
        </div>

        <div className="flex flex-wrap gap-1.5 mb-3">
          {[
            ['', t('superuser.common.all')],
            ...['trial', 'active', 'trial_expired', 'past_due', 'suspended', 'cancelled']
              .map((code) => [code, t(`superuser.licenseStatus.${code}`)]),
          ].map(([v, lab]) => (
            <button key={v}
                    onClick={() => setStatusFilter(v)}
                    className={`text-xs px-3 py-1 rounded-full border transition ${
                      statusFilter === v
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                    }`}>
              {lab}
            </button>
          ))}
        </div>

        <DataTable
          id="superuser-tenants"
          rows={tenants}
          loading={loading}
          rowKey="tenant_id"
          exportName="firmalar"
          emptyText={t('superuser.tenants.noMatch')}
          columns={tenantColumns}
          rowActions={(tn) => (
            <span className="whitespace-nowrap text-xs space-x-2">
              <a href={`/users?tenant_id=${encodeURIComponent(tn.tenant_id)}`}
                 className="text-blue-600 hover:underline">{t('superuser.tenants.users')}</a>
              <a href={`/companies?focus=${encodeURIComponent(tn.tenant_id)}`}
                 className="text-blue-600 hover:underline">{t('superuser.tenants.company')}</a>
            </span>
          )}
        />
      </div>
    </div>
  );
}

/**
 * Bayi Başvuruları tab — partner application queue. Reads from
 * /api/partner-applications (#480) and lets operator set status
 * (pending → contacted → approved / rejected) with notes.
 */
function PartnersTab() {
  const { t } = useTranslation();
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [operatorNotify, setOperatorNotify] = useState(null);
  const [resending, setResending] = useState(null);

  // Labels: superuser.partners.types.<type> / superuser.partners.statuses.<status>.
  const PARTNER_TYPES = ['reseller', 'integrator', 'consultant'];
  const STATUS_COLOR = {
    pending: 'bg-blue-100 text-blue-800',
    contacted: 'bg-amber-100 text-amber-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
  };

  const reload = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const { data } = await axios.get(`${API_URL}/api/partner-applications?${params}`);
      setRows(data.rows || []);
      setOperatorNotify(data.operator_notification || null);
    } catch (e) {
      // silent — empty state will explain
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { reload(); }, [reload]);

  const setStatus = async (id, status) => {
    const notes = status === 'rejected'
      ? (window.prompt(t('superuser.partners.rejectReason'), '') || '').trim()
      : '';
    try {
      await axios.patch(`${API_URL}/api/partner-applications/${id}/status`, { status, notes });
      reload();
    } catch (e) {
      window.alert(e?.response?.data?.detail || t('superuser.common.updateFailed'));
    }
  };

  const resendNotification = async (id) => {
    setResending(id);
    try {
      const { data } = await axios.post(`${API_URL}/api/partner-applications/${id}/resend-notification`, {});
      setRows((a) => a.map((r) => (r.id === id ? { ...r, notifications: data.notifications } : r)));
      if (data.ok) {
        toast.success(t('superuserNotify.resend_ok'));
      } else {
        const err = [...new Set(Object.values(data.result || {}).map((v) => v.error).filter(Boolean))].join('; ');
        toast.error(t('superuserNotify.resend_fail', { error: err || '?' }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('superuserNotify.resend_fail', { error: '?' }));
    } finally {
      setResending(null);
    }
  };

  const deleteRow = async (id, company) => {
    if (!window.confirm(t('superuser.partners.deleteConfirm', { company }))) return;
    try {
      await axios.delete(`${API_URL}/api/partner-applications/${id}`);
      setRows((a) => a.filter((r) => r.id !== id));
    } catch (e) {
      window.alert(e?.response?.data?.detail || t('superuser.common.deleteFailed'));
    }
  };

  const fmtDate = (s) => (s ? String(s).slice(0, 16).replace('T', ' ') : '—');

  const partnerStatus = (r) => (STATUS_COLOR[r.status] ? r.status : 'pending');
  const partnerType = (r) => (PARTNER_TYPES.includes(r.partner_type) ? t(`superuser.partners.types.${r.partner_type}`) : r.partner_type);
  const partnerColumns = [
    { key: 'company_name', header: t('superuser.tenants.company'), sortable: true,
      render: (r) => (
        <div className="font-medium">
          {r.company_name}
          {r.experience && (
            <div className="text-xs text-gray-500 mt-0.5 max-w-xs font-normal">
              {r.experience.slice(0, 120)}{r.experience.length > 120 ? '…' : ''}
            </div>
          )}
        </div>
      ) },
    { key: 'contact_name', header: t('superuser.partners.contact'), sortable: true,
      value: (r) => [r.contact_name, r.email, r.phone].filter(Boolean).join(' '),
      render: (r) => (
        <div className="text-gray-600">
          <div>{r.contact_name}</div>
          <div className="text-xs">{r.email}</div>
          <div className="text-xs">{r.phone}</div>
        </div>
      ) },
    { key: 'partner_type', header: t('superuser.partners.type'), sortable: true, value: partnerType,
      render: (r) => <span className="text-xs">{partnerType(r)}</span> },
    { key: 'region', header: t('superuser.partners.region'), sortable: true,
      render: (r) => <span className="text-xs">{r.region || '—'}</span> },
    { key: 'status', header: t('superuser.licenseHistory.status'), sortable: true,
      value: (r) => t(`superuser.partners.statuses.${partnerStatus(r)}`),
      render: (r) => {
        const sCode = partnerStatus(r);
        const ns = notificationState(r.notifications);
        return (
          <div>
            <span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLOR[sCode]}`}>{t(`superuser.partners.statuses.${sCode}`)}</span>
            {r.operator_notes && (
              <div className="text-[10px] text-gray-500 mt-1 max-w-[160px]">{r.operator_notes}</div>
            )}
            <div className="mt-1 flex flex-col items-start gap-0.5">
              {ns.failed.map((f) => (
                <span key={f.channel} title={f.error}
                      data-testid={`notify-failed-${f.channel}`}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-800">
                  {f.channel === 'operator'
                    ? t('superuserNotify.badge_operator_failed')
                    : t('superuserNotify.badge_applicant_failed')}
                </span>
              ))}
              {ns.operatorSent && (
                <span title={ns.operatorTo.join(', ')} className="text-[10px] text-green-700">
                  {t('superuserNotify.badge_ok')}
                </span>
              )}
              {!ns.recorded && (
                <span className="text-[10px] text-gray-400">
                  {t('superuserNotify.badge_unknown')}
                </span>
              )}
            </div>
          </div>
        );
      } },
    { key: 'created_at', header: t('superuser.partners.applied'), sortable: true,
      render: (r) => <span className="text-xs text-gray-500">{fmtDate(r.created_at)}</span> },
  ];

  return (
    <div className="space-y-6 mt-6">
      <div className="card p-5 space-y-4">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h2 className="section-title">🤝 {t('superuser.partners.title')}</h2>
            <p className="text-xs text-gray-500">
              {t('superuser.partners.help')}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {[['', t('superuser.common.all')],
              ...['pending', 'contacted', 'approved', 'rejected'].map((code) => [code, t(`superuser.partners.statuses.${code}`)]),
            ].map(([v, lab]) => (
              <button key={v} onClick={() => setStatusFilter(v)}
                      className={`text-xs px-3 py-1 rounded-full border transition ${
                        statusFilter === v
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                      }`}>
                {lab}
              </button>
            ))}
          </div>
        </div>

        {operatorNotify && !operatorNotify.configured && (
          <div className="text-sm rounded-lg border border-red-200 bg-red-50 text-red-800 px-3 py-2" data-testid="notify-missing-banner">
            {t('superuserNotify.partners_banner_none')}
          </div>
        )}
        {operatorNotify && operatorNotify.configured && (
          <div className="text-xs text-gray-500">
            {t('superuserNotify.partners_banner_ok', { emails: (operatorNotify.emails || []).join(', ') })}
          </div>
        )}

        <DataTable
          id="superuser-partners"
          rows={rows}
          loading={loading}
          rowKey="id"
          exportName="bayi-basvurulari"
          emptyText={t('superuser.partners.noMatch')}
          columns={partnerColumns}
          rowActions={(r) => {
            const ns = notificationState(r.notifications);
            return (
              <span className="space-x-1 text-xs whitespace-nowrap">
                {r.status !== 'contacted' && (
                  <button onClick={() => setStatus(r.id, 'contacted')}
                          className="text-amber-700 hover:underline">{t('superuser.partners.markContacted')}</button>
                )}
                {r.status !== 'approved' && (
                  <button onClick={() => setStatus(r.id, 'approved')}
                          className="text-green-700 hover:underline">{t('superuser.partners.approve')}</button>
                )}
                {r.status !== 'rejected' && (
                  <button onClick={() => setStatus(r.id, 'rejected')}
                          className="text-red-700 hover:underline">{t('superuser.partners.reject')}</button>
                )}
                {ns.canResend && (
                  <button onClick={() => resendNotification(r.id)} disabled={resending === r.id}
                          className="text-blue-700 hover:underline disabled:opacity-50">
                    {t('superuserNotify.resend')}
                  </button>
                )}
                <button onClick={() => deleteRow(r.id, r.company_name)}
                        className="text-gray-500 hover:text-gray-800" title={t('superuser.common.delete')}>🗑</button>
              </span>
            );
          }}
        />
      </div>
    </div>
  );
}

export default SuperuserPanel;
